
import { useState, useEffect } from 'react';

// Hex Bolt Corner Component
const HexBolt = ({ position }: { position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' }) => {
  const positions = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2',
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2',
  };

  return (
    <div className={`absolute w-4 h-4 ${positions[position]}`}>
      <svg viewBox="0 0 24 24" className="w-full h-full drop-shadow-md">
        {/* Outer hexagon - bolt head */}
        <polygon
          points="12,2 21,7 21,17 12,22 3,17 3,7"
          fill="#a8a29e"
        />
        {/* Top bevel highlight */}
        <polygon
          points="12,2 21,7 12,7 3,7"
          fill="#d6d3d1"
        />
        {/* Left face */}
        <polygon
          points="3,7 12,7 12,12 3,17"
          fill="#a8a29e"
        />
        {/* Right face */}
        <polygon
          points="21,7 12,7 12,12 21,17"
          fill="#78716c"
        />
        {/* Bottom shadow */}
        <polygon
          points="12,22 21,17 12,17 3,17"
          fill="#57534e"
        />
        {/* Inner hexagon socket */}
        <polygon
          points="12,7 17,10 17,14 12,17 7,14 7,10"
          fill="#44403c"
        />
        {/* Socket depth */}
        <polygon
          points="12,8 16,10.5 16,13.5 12,16 8,13.5 8,10.5"
          fill="#292524"
        />
        {/* Socket highlight */}
        <polygon
          points="12,8 16,10.5 12,10.5 8,10.5"
          fill="#57534e"
        />
      </svg>
    </div>
  );
};

// Gold Dollar Sign Corner Component
const GoldDollarSign = ({ position }: { position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' }) => {
  const positions = {
    'top-left': 'top-1.5 left-1.5',
    'top-right': 'top-1.5 right-1.5',
    'bottom-left': 'bottom-1.5 left-1.5',
    'bottom-right': 'bottom-1.5 right-1.5',
  };

  return (
    <div className={`absolute ${positions[position]} w-6 h-6 flex items-center justify-center`}>
      <span 
        className="text-2xl font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]"
        style={{ 
          background: 'linear-gradient(135deg, #D4AF37 0%, #F4E5A1 50%, #D4AF37 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          filter: 'drop-shadow(0 1px 2px rgba(212, 175, 55, 0.5))'
        }}
      >
        $
      </span>
    </div>
  );
};

// Ornamental Spear Divider Component
const OrnamentalDivider = () => (
  <div className="mt-6 mx-auto flex items-center justify-center gap-0 w-64">
    {/* Left ornamental line */}
    <div className="flex-1 h-[2px] bg-gradient-to-r from-transparent via-stone-500/60 to-stone-500"></div>
    <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-stone-500/40 -mt-1"></div>
    
    {/* Left spear point */}
    <svg viewBox="0 0 20 12" className="w-4 h-2.5 text-stone-500">
      <path d="M0 6 L15 6 L20 3 L15 6 L20 9 L15 6" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
    
    {/* Center diamond crest */}
    <div className="mx-1.5 relative">
      <svg viewBox="0 0 24 24" className="w-5 h-5 text-stone-500">
        <path d="M12 2 L22 12 L12 22 L2 12 Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 6 L18 12 L12 18 L6 12 Z" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.6" />
        <circle cx="12" cy="12" r="2" fill="currentColor" opacity="0.8" />
      </svg>
    </div>
    
    {/* Right spear point */}
    <svg viewBox="0 0 20 12" className="w-4 h-2.5 text-stone-500 rotate-180">
      <path d="M0 6 L15 6 L20 3 L15 6 L20 9 L15 6" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
    
    {/* Right ornamental line */}
    <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-stone-500/40 -mt-1"></div>
    <div className="flex-1 h-[2px] bg-gradient-to-l from-transparent via-stone-500/60 to-stone-500"></div>
  </div>
);

export default function HomePage() {
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    comment: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [leftHovered, setLeftHovered] = useState(false);
  const [rightHovered, setRightHovered] = useState(false);

  // Inject keyframes into document
  useEffect(() => {
    const styleId = 'pulse-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes pulseUp {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        @keyframes pulseDown {
          0%, 100% { transform: scale(1.02); }
          50% { transform: scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('comment', formData.comment);

      const response = await fetch('https://readdy.ai/api/form/d61oqap50i17hner1pjg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', comment: '' });
        setTimeout(() => {
          setShowContactForm(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 via-white to-stone-50 flex flex-col relative overflow-hidden">
      {/* Background Pattern Image */}
      <div 
        className="fixed inset-0 pointer-events-none z-0 opacity-30"
        style={{
          backgroundImage: 'url(https://static.readdy.ai/image/c5e888261e0c20043640d243c83b334e/969ea7ea6a875e59a9e61ebe8ea46e74.png)',
          backgroundRepeat: 'repeat',
          backgroundSize: 'auto',
          backgroundPosition: 'center'
        }}
      />

      {/* Watermark Background T */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <span 
          className="text-[35rem] font-serif font-bold text-stone-300/25 leading-none"
          style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
        >
          T
        </span>
      </div>

      {/* Header */}
      <header className="py-10 text-center relative z-10">
        {/* Top gold divider line */}
        <div className="flex justify-center mb-4">
          <div className="w-36 h-[1px] bg-gradient-to-r from-transparent via-stone-500/80 to-transparent"></div>
        </div>
        
        <h1 
          className="text-4xl md:text-5xl font-bold text-slate-700 tracking-[0.15em] uppercase"
          style={{ 
            fontFamily: 'Cinzel, Playfair Display, Georgia, serif',
            textShadow: '0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.05)'
          }}
        >
          Thorburn.com
        </h1>
        
        {/* Bottom gold divider line */}
        <div className="flex justify-center mt-4">
          <div className="w-36 h-[1px] bg-gradient-to-r from-transparent via-stone-500/80 to-transparent"></div>
        </div>
        
        {/* Ornamental Spear Divider */}
        <OrnamentalDivider />
      </header>

      {/* Main Content - Increased padding */}
      <main className="flex-1 flex items-center justify-center px-6 pb-20 pt-4 relative z-10">
        <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
          {/* Left Side - Thorburn Family */}
          <div className="flex flex-col items-center text-center space-y-7">
            <h2 
              className="text-xl md:text-2xl font-semibold text-stone-800 tracking-wide"
              style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            >
              Thorburn
              <br />
              <span className="text-xl md:text-2xl">Family Website</span>
            </h2>

            {/* Card with gold border and filigree corners */}
            <div 
              className="w-64 h-64 bg-white rounded-xl border border-stone-400/60 shadow-[0_8px_40px_rgba(0,0,0,0.06)] p-5 flex items-center justify-center transition-all duration-500 group relative cursor-pointer"
              onMouseEnter={() => setLeftHovered(true)}
              onMouseLeave={() => setLeftHovered(false)}
              style={{
                animation: leftHovered ? 'none' : 'pulseUp 6s ease-in-out infinite',
                transform: leftHovered ? 'scale(1.04) translateY(-8px)' : 'scale(1)',
                boxShadow: leftHovered 
                  ? '0 20px 60px rgba(120, 113, 108, 0.25), 0 0 40px rgba(120, 113, 108, 0.15)' 
                  : '0 8px 40px rgba(0,0,0,0.06)',
              }}
            >
              {/* Filigree Corners */}
              <HexBolt position="top-left" />
              <HexBolt position="top-right" />
              <HexBolt position="bottom-left" />
              <HexBolt position="bottom-right" />
              
              {/* Image with outer glow */}
              <div className="w-full h-full relative">
                <div className={`absolute inset-0 bg-stone-400/20 blur-2xl rounded-full scale-75 transition-opacity duration-500 ${leftHovered ? 'opacity-100' : 'opacity-0'}`}></div>
                <img
                  src="https://static.readdy.ai/image/c5e888261e0c20043640d243c83b334e/e34ac5e8bcf2cd38d8140bb486e3d540.png"
                  alt="Thorburn Family Crest"
                  title="Thorburn Family Crest"
                  className="w-full h-full object-contain relative z-10 drop-shadow-[0_4px_20px_rgba(120,113,108,0.3)]"
                />
              </div>
            </div>

            {/* Royal Double-Border Button */}
            <button
              onClick={() => setShowContactForm(true)}
              className="relative px-8 py-3 bg-transparent text-stone-600 text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer tracking-wider group hover:text-stone-700"
              style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            >
              {/* Outer border */}
              <span className="absolute inset-0 border-2 border-stone-500 rounded-lg group-hover:border-yellow-300"></span>
              {/* Inner border */}
              <span className="absolute inset-[4px] border border-stone-400/50 rounded-md group-hover:border-yellow-200/50"></span>
              {/* Gold gradient background on hover */}
              <span className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-yellow-100 via-yellow-200 to-amber-100"></span>
              <span className="relative">{`Who's Your Granddaddy?`}</span>
            </button>
          </div>

          {/* Right Side - Thorburn Financial Services */}
          <div className="flex flex-col items-center text-center space-y-7">
            <h2 
              className="text-xl md:text-2xl font-semibold text-stone-800 tracking-wide"
              style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            >
              Thorburn
              <br />
              <span className="text-xl md:text-2xl">Financial Services</span>
            </h2>

            {/* Card with gold border and dollar sign corners */}
            <a
              href="https://thorburn.financial"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-64 h-64 bg-white rounded-xl border border-stone-400/60 shadow-[0_8px_40px_rgba(0,0,0,0.06)] p-5 flex items-center justify-center transition-all duration-500 cursor-pointer group relative"
              onMouseEnter={() => setRightHovered(true)}
              onMouseLeave={() => setRightHovered(false)}
              style={{
                animation: rightHovered ? 'none' : 'pulseDown 6s ease-in-out infinite',
                transform: rightHovered ? 'scale(1.04) translateY(-8px)' : 'scale(1)',
                boxShadow: rightHovered 
                  ? '0 20px 60px rgba(120, 113, 108, 0.25), 0 0 40px rgba(120, 113, 108, 0.15)' 
                  : '0 8px 40px rgba(0,0,0,0.06)',
              }}
            >
              {/* Gold Dollar Sign Corners */}
              <GoldDollarSign position="top-left" />
              <GoldDollarSign position="top-right" />
              <GoldDollarSign position="bottom-left" />
              <GoldDollarSign position="bottom-right" />
              
              {/* Image with outer glow */}
              <div className="w-full h-full relative">
                <div className={`absolute inset-0 bg-stone-400/20 blur-2xl rounded-full scale-75 transition-opacity duration-500 ${rightHovered ? 'opacity-100' : 'opacity-0'}`}></div>
                <img
                  src="https://static.readdy.ai/image/c5e888261e0c20043640d243c83b334e/f4837732f3f5a310bbbc8f9e0257921a.png"
                  alt="Thorburn Financial Services Logo"
                  title="Thorburn Financial Services"
                  className="w-full h-full object-contain relative z-10 drop-shadow-[0_4px_20px_rgba(120,113,108,0.3)]"
                />
              </div>
            </a>

            {/* Royal Double-Border Button */}
            <a
              href="https://thorburn.financial"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="relative px-8 py-3 bg-transparent text-stone-600 text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer tracking-wider group hover:text-white"
              style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            >
              {/* Outer border */}
              <span className="absolute inset-0 border-2 border-stone-500 rounded-lg group-hover:border-blue-400"></span>
              {/* Inner border */}
              <span className="absolute inset-[4px] border border-stone-400/50 rounded-md group-hover:border-blue-300/50"></span>
              {/* Blue gradient background on hover */}
              <span className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-400"></span>
              <span className="relative">Explore TFS Website</span>
            </a>
          </div>
        </div>
      </main>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-stone-900/30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 relative border border-stone-400/40">
            {/* Modal Filigree Corners */}
            <HexBolt position="top-left" />
            <HexBolt position="top-right" />
            <HexBolt position="bottom-left" />
            <HexBolt position="bottom-right" />
            
            <button
              onClick={() => setShowContactForm(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-stone-100 hover:bg-stone-200 text-stone-500 hover:text-stone-700 rounded-full cursor-pointer transition-all z-10"
              aria-label="Close"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <h3 
              className="text-xl font-semibold text-stone-900 mb-2 text-center tracking-wide"
              style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            >
              Tell Us About Yourself
            </h3>
            {/* Ornamental mini divider in modal */}
            <div className="mb-6 mx-auto flex items-center justify-center gap-2 w-32">
              <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-stone-500"></div>
              <svg viewBox="0 0 16 16" className="w-3 h-3 text-stone-500">
                <path d="M8 1 L15 8 L8 15 L1 8 Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="8" cy="8" r="1.5" fill="currentColor" />
              </svg>
              <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-stone-500"></div>
            </div>

            <form id="thorburn-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label 
                  htmlFor="name" 
                  className="block text-xs font-medium text-stone-600 mb-1.5 tracking-wide"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-400/40 focus:border-stone-400 outline-none text-sm transition-all"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                />
              </div>

              <div>
                <label 
                  htmlFor="email" 
                  className="block text-xs font-medium text-stone-600 mb-1.5 tracking-wide"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-400/40 focus:border-stone-400 outline-none text-sm transition-all"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                />
              </div>

              <div>
                <label 
                  htmlFor="comment" 
                  className="block text-xs font-medium text-stone-600 mb-1.5 tracking-wide"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Comment
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  required
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-400/40 focus:border-stone-400 outline-none resize-none text-sm transition-all"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                />
                <p className="text-xs text-stone-400 mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {formData.comment.length}/500 characters
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-emerald-50 text-emerald-800 px-3 py-2 rounded-lg text-xs text-center border border-emerald-200">
                  Message sent successfully!
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 text-red-800 px-3 py-2 rounded-lg text-xs text-center border border-red-200">
                  Failed to send message. Please try again.
                </div>
              )}

              {/* Royal Double-Border Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="relative w-full px-5 py-2.5 bg-transparent text-stone-600 text-sm font-medium rounded-lg hover:bg-stone-500 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap tracking-wider"
                style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
              >
                {/* Outer border */}
                <span className="absolute inset-0 border-2 border-stone-500 rounded-lg"></span>
                {/* Inner border */}
                <span className="absolute inset-[4px] border border-stone-400/50 rounded-md"></span>
                <span className="relative">{isSubmitting ? 'Sending...' : 'Submit'}</span>
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 text-center text-xs text-stone-500 border-t border-stone-200/60 relative z-10">
        <p className="tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          © {new Date().getFullYear()} Thorburn.com. All rights reserved. | Powered by Atlas Digital
        </p>
      </footer>
    </div>
  );
}
